import { API_BASE } from './api-base';
import { useAuthStore } from '@/store/auth';
import { adoptNewerSession, endSession } from './session';

/**
 * Tiny fetch wrapper used by every API call in the app.
 * - Prepends API_BASE so we don't repeat /ukv-payroll/api everywhere
 * - Injects Authorization: Bearer <token> from the auth store
 * - Throws ApiError on non-2xx so TanStack Query treats it as a failure
 * - Ends the session when the server rejects the token, so AppShell
 *   redirects to /login (see `authedFetch`)
 */

export const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please sign in again.';

type DetailEntry = { loc?: unknown; msg?: unknown; field?: unknown; message?: unknown };

/**
 * The text a person should read for a failed call's body, or null when it carries none.
 *
 * `detail` comes as a plain string, an object — a refused permission is
 * {code, permission, reason, message}, a master's rules {errors: {field: message}},
 * a package import {problems: [...]} — or a list: FastAPI's request validation
 * gives [{loc, msg}], the OT rule / shift endpoints [{field, message}]. An object
 * shown as-is reads "[object Object]" in text and crashes React as a child.
 */
function readableDetail(status: number, body: unknown): string | null {
  const detail = (body as { detail?: unknown } | null | undefined)?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length) {
    const lines = (detail as Array<DetailEntry | string>).map((e) => {
      if (typeof e === 'string') return e;
      const where = Array.isArray(e?.loc)
        ? e.loc.filter((p) => p !== 'body' && p !== 'query' && p !== 'path').join('.')
        : typeof e?.field === 'string' ? e.field : '';
      const text = typeof e?.msg === 'string' ? e.msg : typeof e?.message === 'string' ? e.message : null;
      return where ? `${where}: ${text ?? 'invalid'}` : (text ?? 'Invalid value');
    });
    return `Validation failed: ${lines.join('; ')}`;
  }
  if (detail && typeof detail === 'object') {
    const { message, errors, problems } = detail as { message?: unknown; errors?: unknown; problems?: unknown };
    if (typeof message === 'string' && message) return message;
    if (errors && typeof errors === 'object') return Object.values(errors).join(' ');
    if (Array.isArray(problems) && problems.length) return problems.join(' ');
  }
  // 5xx with no usable detail (e.g. a bare proxy 500 / crash) — give an
  // actionable message instead of the cryptic "HTTP 500".
  if (status >= 500) {
    return `The server hit an error (HTTP ${status}) and couldn't complete the request. `
      + `This is a server-side issue, not your input. Please try again. `
      + `If it keeps happening the backend may be unavailable.`;
  }
  return null;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
    message?: string,
  ) {
    // The readable detail becomes the message, so every screen that shows
    // `error.message` says why the call failed instead of "HTTP 403".
    super(message ?? readableDetail(status, body) ?? `HTTP ${status}`);
    this.name = 'ApiError';
  }

  get detailMessage(): string {
    return readableDetail(this.status, this.body) ?? this.message;
  }
}

/** The text to show for any failed call: an API error's readable detail, else the error's own message. */
export function errorMessage(err: unknown, fallback = 'Something went wrong.'): string {
  if (err instanceof ApiError) return err.detailMessage;
  return err instanceof Error && err.message ? err.message : fallback;
}

type Options = RequestInit & { params?: Record<string, string | number | boolean | undefined | null> };

function buildUrl(path: string, params?: Options['params']): string {
  const url = `${API_BASE}${path.startsWith('/') ? '' : '/'}${path}`;
  if (!params) return url;
  const qs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');
  // A path may already carry a query (an entity picked in the header); add to it rather than start another.
  return qs ? `${url}${url.includes('?') ? '&' : '?'}${qs}` : url;
}

/**
 * Did the server refuse this request's session (as opposed to its content)?
 *
 * 401 is the contract. 400 TENANT_UNRESOLVED is what servers from before the
 * tenant middleware learned to say 401 answered for an expired token — they
 * could no longer read the workspace out of it. An authenticated request the
 * server cannot place in any workspace is a dead session either way.
 */
async function isSessionRejected(res: Response): Promise<boolean> {
  if (res.status === 401) return true;
  if (res.status !== 400) return false;
  const body = await res.clone().json().catch(() => null) as { code?: string } | null;
  return body?.code === 'TENANT_UNRESOLVED';
}

/**
 * `fetch` with the session's bearer token attached, and ONE shared answer to
 * "the server no longer accepts this session": end it, so the user lands on
 * the login page instead of an error. Every authenticated request — JSON,
 * uploads and file downloads — must go through here; a raw `fetch` with a
 * hand-attached token shows the user a bare 401 when the session runs out.
 *
 * Requests the caller signs itself (its own Authorization header) and
 * anonymous ones (the login form) are passed through untouched — a 401 on a
 * wrong password is an answer for the form, not the end of a session.
 *
 * Returns the Response for every other status; callers handle !ok themselves.
 */
export async function authedFetch(url: string, init: RequestInit = {}): Promise<Response> {
  for (let attempt = 0; ; attempt += 1) {
    const token = useAuthStore.getState().token;
    const headers = new Headers(init.headers);
    const attached = !!token && !headers.has('Authorization');
    if (attached) headers.set('Authorization', `Bearer ${token}`);

    const res = await fetch(url, { ...init, headers });
    if (!attached || !(await isSessionRejected(res))) return res;

    // Another tab signed in again while this request was in flight: the
    // server rejected the old token, not the user. The request never reached
    // its handler (auth runs first), so one retry is safe for any method.
    if (attempt === 0 && adoptNewerSession(token!)) continue;

    endSession('expired');
    throw new ApiError(401, { detail: SESSION_EXPIRED_MESSAGE }, SESSION_EXPIRED_MESSAGE);
  }
}

/** Parse a response body the way every helper here does. */
async function readBody(res: Response): Promise<unknown> {
  const contentType = res.headers.get('content-type') || '';
  return contentType.includes('application/json')
    ? res.json().catch(() => null)
    : res.text();
}

export async function apiFetch<T = unknown>(path: string, options: Options = {}): Promise<T> {
  const { params, headers, ...rest } = options;
  const finalHeaders = new Headers(headers);
  if (rest.body && !finalHeaders.has('Content-Type') && !(rest.body instanceof FormData)) {
    finalHeaders.set('Content-Type', 'application/json');
  }

  const res = await authedFetch(buildUrl(path, params), { ...rest, headers: finalHeaders });
  if (res.status === 204) return undefined as T;
  const body = await readBody(res);
  if (!res.ok) {
    throw new ApiError(res.status, body);
  }
  return body as T;
}

/**
 * Multipart helper for file uploads. Same auth/error contract as `apiFetch`,
 * but the body is a FormData so the browser sets Content-Type with boundary.
 */
export async function apiUpload<T = unknown>(path: string, fd: FormData,
  params?: Options['params']): Promise<T> {
  const res = await authedFetch(buildUrl(path, params), { method: 'POST', body: fd });
  const body = await readBody(res);
  if (!res.ok) throw new ApiError(res.status, body);
  return body as T;
}

/** Convenience helpers — keeps call sites tidy.
 *
 *  Mutations accept an optional `ifMatch` string. When provided, it's sent
 *  as the `If-Match` header for optimistic-locking precondition checks
 *  (backend returns HTTP 412 when the row changed since the client loaded
 *  it). Pass the row's `updated_at` from the previous GET response. */
export const api = {
  get:    <T = unknown>(path: string, params?: Options['params']) =>
            apiFetch<T>(path, { method: 'GET', params }),
  post:   <T = unknown>(path: string, body?: unknown, ifMatch?: string) =>
            apiFetch<T>(path, {
              method: 'POST',
              body: body == null ? undefined : JSON.stringify(body),
              headers: ifMatch ? { 'If-Match': ifMatch } : undefined,
            }),
  put:    <T = unknown>(path: string, body?: unknown, ifMatch?: string) =>
            apiFetch<T>(path, {
              method: 'PUT',
              body: body == null ? undefined : JSON.stringify(body),
              headers: ifMatch ? { 'If-Match': ifMatch } : undefined,
            }),
  patch:  <T = unknown>(path: string, body?: unknown, ifMatch?: string) =>
            apiFetch<T>(path, {
              method: 'PATCH',
              body: body == null ? undefined : JSON.stringify(body),
              headers: ifMatch ? { 'If-Match': ifMatch } : undefined,
            }),
  delete: <T = unknown>(path: string, params?: Options['params']) =>
            apiFetch<T>(path, { method: 'DELETE', params }),
};
